import { useEffect, useState } from 'react';
import { ListingType } from '../types/ListingTypes';
import { AccountType } from '../types/AccountTypes';
import { TYPE_CONSTANT } from '../constant/Web_constant';

interface UserData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar?: string;
}

interface ListingData extends ListingType {}

interface AccountData extends AccountType {}

export function useTradlyChat() {
  const [isLoaded, setIsLoaded] = useState(false);
  let userDetails = localStorage.getItem('user_details')
    ? JSON.parse(localStorage.getItem('user_details'))
    : null;

  // Load chat script once
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !(window as any).TradlyChat &&
      TYPE_CONSTANT?.ALL_INTEGRATIONS?.['tradly_chat']?.enabled === 'true'
    ) {
      const script = document.createElement('script');
      //chat.tradly.app
      script.src = 'https://chat.tradly.app/loader.js';
      script.async = true;
      script.onload = () => {
        setIsLoaded(true);
        console.log('✅ TradlyChat loaded');
      };
      document.head.appendChild(script);
    } else if ((window as any).TradlyChat) {
      setIsLoaded(true);
    }
  }, []);

  // Initialize chat with user (call after login)
  const initializeChat = (
    userData: UserData,
    firebaseToken: string,
    tenant_name: string
  ) => {
    if ((window as any).TradlyChat && isLoaded) {
      (window as any).TradlyChat.init({
        firebaseToken: firebaseToken,
        userId: userData.id,
        userData: userData,
        tenantId: tenant_name,
      });
    }
  };

  // Set listing context (call on product page)
  const setListingContext = (listingData: ListingData) => {
    if (
      (window as any).TradlyChat &&
      isLoaded &&
      userDetails &&
      userDetails?.key?.firebase_token
    ) {
      (window as any).TradlyChat.setListingContext({
        listingId: listingData.id,
        accountId: listingData.account.id,
        ownerDetails: {
          id: listingData.user.id,
          first_name: listingData.user.first_name,
          last_name: listingData.user.last_name,
          email: listingData.user.email,
          profile_pic: listingData.user.profile_pic,
          birth_date: null,
        },
        productDetails: {
          id: listingData.id,
          title: listingData.title,
          price: listingData.offer_price.formatted,
          images: listingData.images,
        },
      });
      console.log('✅ Listing context set for:', listingData.title);
    }
  };
  // Set account context (call on account details page)
  const setAccountContext = (accountData: AccountData) => {
    if (
      (window as any).TradlyChat &&
      isLoaded &&
      userDetails &&
      userDetails?.key?.firebase_token
    ) {
      (window as any).TradlyChat.setAccountContext({
        accountId: accountData.id,
        ownerDetails: {
          id: accountData.user.id,
          first_name: accountData.user.first_name,
          last_name: accountData.user.last_name,
          email: accountData.user.email,
          profile_pic: accountData.user.profile_pic,
          birth_date: null,
        },
        accountDetails: {
          id: accountData.id,
          name: accountData.name,
          location: accountData.location,
          images: accountData.images,
          description: accountData.description,
          coordinates: accountData.coordinates,
          shipping_methods: accountData.shipping_methods,
          commission_percent: accountData.commission_percent,
        },
      });
      console.log('✅ Account context set for:', accountData.name);
    }
  };

  // Update user details (call when user profile changes)
  const updateUser = (userData: UserData) => {
    if ((window as any).TradlyChat && isLoaded) {
      (window as any).TradlyChat.updateUser({
        userId: userData.id,
        userData: {
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
          avatar: userData.avatar,
        },
      });
      console.log('✅ User updated:', userData.first_name);
    }
  };

  // Logout (call when user logs out)
  const logout = () => {
    if ((window as any).TradlyChat && isLoaded) {
      (window as any).TradlyChat.logout();
      console.log('✅ Chat logged out');
    }
  };

  // Refresh token (call when you get new Firebase token)
  const refreshAuth = (newToken: string) => {
    if ((window as any).TradlyChat && isLoaded) {
      (window as any).TradlyChat.refreshAuth(newToken);
      console.log('✅ Auth refreshed');
    }
  };

  return {
    isLoaded,
    initializeChat,
    setListingContext,
    setAccountContext,
    updateUser,
    logout,
    refreshAuth,
  };
}
